import React from 'react';
import { Row, Col } from 'reactstrap';
import moment from 'moment';
import { faCalendarTimes } from '@fortawesome/free-solid-svg-icons';
import { getInputFormat } from '@s20/common/util/date';
import { httpClient } from '@s20/services/httpClient';
import SlideForm from '../common/forms/SlideForm';
import {
    Input,
    onFieldChange,
    toasty,
} from '../common/forms/FormElements';
import CommonContext, { ApiRoutes } from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { LongTermStatusType } from '../longTermStatus/LongTermStatus';
import { EquipmentLongTermStatus } from './EquipmentLongTermStatus';

export class EquipmentLongTermStatusForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            formValidated: false,
            errors: {},
            endDateError: '',
            equipmentLongTermStatus:
                this.props.equipmentLongTermStatus
                ?? new EquipmentLongTermStatus(),
            longTermStatuses: [],
            isValidated: false,
            formTitle: '',
            loading: false,
            saving: false,
        };
        this.onSubmit = this.onSubmit.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onLongTermStatusChanged = this.onLongTermStatusChanged.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onChange = onFieldChange;

    onClose(response) {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    }

    onDelete = async () => {
        const { equipmentLongTermStatus } = this.state;

        const response = await httpClient
            .delete(
                ApiRoutes.equipmentLongTermStatus.delete(
                    equipmentLongTermStatus.id,
                ),
            )
            .catch(this.handleSaveError);

        if (response && !isNaN(response)) {
            toasty.success(
                'Equipment Long Term Status deleted',
                'Equipment Long Term Status deleted successfully.',
            );

            this.onClose(response);
            this.props.onSaveCallback();
        }
    };

    onLongTermStatusChanged = (selection) => {
        const { equipmentLongTermStatus } = this.state;

        equipmentLongTermStatus.longTermStatusId = selection.id;

        this.setState({ equipmentLongTermStatus });
    };

    handleDateChange = (e) => {
        const { name, value } = e.target;
        const { equipmentLongTermStatus } = this.state;

        this.setState(
            {
                equipmentLongTermStatus: {
                    ...equipmentLongTermStatus,
                    [name]: value, // Dynamically update either startDate or endDate
                },
            },
            this.validateDates, // Validate after state update
        );
    };

    validateDates = () => {
        const { startDate, endDate } = this.state.equipmentLongTermStatus;

        let endDateError = '';

        if (moment(endDate).isValid() && moment(startDate).isValid()) {
            if (moment(endDate).isBefore(moment(startDate))) {
                endDateError = 'End Date cannot be earlier than Start Date.';
            }
        }

        this.setState({ endDateError });
    };

    onSubmit = async () => {
        const { equipmentLongTermStatus, endDateError } = this.state;

        if (endDateError) {
            return;
        }

        if (!equipmentLongTermStatus.endDate) {
            equipmentLongTermStatus.endDate = null;
        }

        this.setState({ errors: {}, endDateError: '', saving: true });

        const isExisting = Boolean(
            equipmentLongTermStatus.id && equipmentLongTermStatus.id > 0,
        );

        const url = isExisting
            ? ApiRoutes.equipmentLongTermStatus.update(equipmentLongTermStatus.id)
            : ApiRoutes.equipmentLongTermStatus.create();

        const fetchType = isExisting
            ? httpClient.types.put
            : httpClient.types.post;

        const response = await httpClient.andGetResponse(
            fetchType,
            url,
            equipmentLongTermStatus,
            'Error Saving Long-Term Status',
            () => this.setState({ saving: false }),
        );

        if (response && !isNaN(response)) {
            this.onClose(response);

            const description = isExisting ? 'updated' : 'created';
            toasty.success(
                `Equipment Long Term Status ${description}`,
                `Equipment Long Term Status ${description} successfully.`,
            );

            this.props.onSaveCallback();
        }
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    isEndDateRequired = () => {
        const { equipmentLongTermStatus, longTermStatuses } = this.state;

        if (
            !equipmentLongTermStatus
            || !equipmentLongTermStatus.longTermStatusId
        ) {
            return false;
        }

        const longTermStatus = longTermStatuses.find(
            (l) => l.id == equipmentLongTermStatus.longTermStatusId,
        );

        return Boolean(longTermStatus && longTermStatus.requiresEndDate);
    };

    open = (statusOrEquipmentId) => {
        try {
            const isNew = typeof statusOrEquipmentId === 'number';

            // Create new or use existing status
            let equipmentLongTermStatus;

            if (isNew) {
                equipmentLongTermStatus = new EquipmentLongTermStatus();
                equipmentLongTermStatus.equipmentId = statusOrEquipmentId;
                equipmentLongTermStatus.startDate = getInputFormat(new Date());
            } else {
                equipmentLongTermStatus = { ...statusOrEquipmentId };
            }

            // Format dates
            equipmentLongTermStatus.startDate = getInputFormat(
                equipmentLongTermStatus.startDate,
            );
            equipmentLongTermStatus.endDate = getInputFormat(
                equipmentLongTermStatus.endDate,
            );

            // Set state and open form
            this.setState({
                equipmentLongTermStatus,
                formTitle: isNew ? 'New Long Term Status' : 'Edit Long Term Status',
                loading: false,
            });

            this.props.toggleShow(true);
            this.context.setFormOpened(true);
        } catch (error) {
            console.error('Error opening equipment long term status form:', error);
            toasty.error('Failed to open status form');
            this.setState({ loading: false });
        }
    };

    openEquipmentLongTermStatus(equipmentLongTermStatus) {
        equipmentLongTermStatus.startDate = getInputFormat(
            equipmentLongTermStatus.startDate,
        );
        equipmentLongTermStatus.endDate = getInputFormat(
            equipmentLongTermStatus.endDate,
        );
        this.setState({ equipmentLongTermStatus });

        this.props.toggleShow(true);
        this.context.setFormOpened(true);
    }

    populateState = async () => {
        const [longTermStatuses] = await Promise.all([
            httpClient.js(ApiRoutes.longTermStatus.byTypeId(LongTermStatusType.Equipment)),
        ]);

        this.setState({ longTermStatuses, loading: false });
    };

    resetForm = () => {
        this.setState({
            formValidated: false,
            equipmentLongTermStatus: new EquipmentLongTermStatus(),
            errors: {},
            endDateError: '',
            isValidated: false,
            validationMessage: null,
        });
    };

    render() {
        const {
            errors,
            validationMessage,
            equipmentLongTermStatus,
            longTermStatuses,
            isValidated,
            endDateError,
            loading,
            saving,
            formTitle,
        } = this.state;
        const { show } = this.props;

        if (!equipmentLongTermStatus) {
            return '';
        }

        const isEndDateRequired = this.isEndDateRequired();

        return (
            <SlideForm
                loading={loading}
                show={show}
                id="equipmentLongTermStatusForm"
                formIcon={faCalendarTimes}
                formTitle={formTitle}
                ref={this.formRef}
                setIsValidated={(val) => this.setState({ isValidated: val })}
                isValidated={isValidated}
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                onDelete={equipmentLongTermStatus.id ? this.onDelete : undefined}
                errors={errors}
                onClearErrors={this.onClearErrors}
                error={validationMessage}
                saveDisabled={saving}
                entityId={equipmentLongTermStatus.id}
            >
                <Row className="pl-3 pr-3 pb-3 mr-0 ml-0">
                    <Col>
                        <ValidatedSelect
                            className="w-auto"
                            isClearable={false}
                            id="longTermStatusesDropdown"
                            name="equipmentLongTermStatus.longTermStatusId"
                            label="Long Term Status"
                            required
                            value={longTermStatuses.find((i) => i.id == equipmentLongTermStatus.longTermStatusId) ?? ''}
                            options={longTermStatuses ?? []}
                            onChange={this.onLongTermStatusChanged}
                            getOptionLabel={(o) => o.name}
                            getOptionValue={(o) => o.id}
                            error="A long term status is required."
                            isDisabled={Boolean(
                                equipmentLongTermStatus.id,
                            )}
                        />

                        <Input
                            label="Start Date"
                            id="startDate"
                            required
                            type="date"
                            name="startDate"
                            value={equipmentLongTermStatus.startDate || ''}
                            onChange={this.handleDateChange}
                            error="Start Date is required."
                        />
                        <Input
                            label="End Date"
                            id="endDate"
                            type="date"
                            required={isEndDateRequired}
                            name="endDate"
                            value={equipmentLongTermStatus.endDate || ''}
                            onChange={this.handleDateChange}
                            error={endDateError}
                        />
                    </Col>
                </Row>
            </SlideForm>
        );
    }
}
